import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePageContent } from "@/hooks/usePageContent";
import usePageTitle from "@/hooks/usePageTitle";
import axios from "axios";
import { 
  ArrowRight, 
  CheckCircle, 
  XCircle, 
  Lightbulb,
  Bird,
  Fish,
  Droplets,
  Eye,
  Target,
  Brain,
  Search,
  ShieldCheck,
  Pause,
  User,
  MessageCircle,
  Footprints
} from "lucide-react";
import { toast } from "sonner";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const PHOTO_1 = `${BASE_URL}/alma-vivu/photos/methodologie-atelier1.png`;
const PHOTO_2 = `${BASE_URL}/alma-vivu/photos/methodologie-atelier2.png`;
const PHOTO_3 = `${BASE_URL}/alma-vivu/photos/methodologie-atelier3.png`;

const GeckoIcon = ({ className }) => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M50 15 C44 15 40 19 40 24 C40 27 41 29 43 31 L43 35 C41 37 39 40 39 44 L39 58 C39 62 41 64 43 66 L43 68 C41 71 40 74 41 78 Q45 86 50 88 Q55 86 59 78 C60 74 59 71 57 68 L57 66 C59 64 61 62 61 58 L61 44 C61 40 59 37 57 35 L57 31 C59 29 60 27 60 24 C60 19 56 15 50 15 Z" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="45" cy="20" r="3" fill="currentColor" />
    <circle cx="55" cy="20" r="3" fill="currentColor" />
    <path d="M39 36 L26 26" strokeWidth="3" />
    <path d="M26 26 L22 22 M26 26 L21 25 M26 26 L23 29" strokeWidth="2.5" />
    <path d="M61 36 L74 26" strokeWidth="3" />
    <path d="M74 26 L78 22 M74 26 L79 25 M74 26 L77 29" strokeWidth="2.5" />
    <path d="M39 60 L26 70" strokeWidth="3" />
    <path d="M26 70 L22 68 M26 70 L21 71 M26 70 L23 74" strokeWidth="2.5" />
    <path d="M61 60 L74 70" strokeWidth="3" />
    <path d="M74 70 L78 68 M74 70 L79 71 M74 70 L77 74" strokeWidth="2.5" />
    <path d="M50 78 Q50 86 55 90 Q62 95 60 98" strokeWidth="3" fill="none" />
  </svg>
);

const biomimicryExamples = [
  { animal: "Le Martin-pêcheur", icon: Bird, innovation: "Shinkansen (TGV japonais)", description: "Le bec du martin-pêcheur a inspiré la forme du nez du train Shinkansen, réduisant le bruit et la consommation d'énergie de 15%.", color: "var(--terracotta)" },
  { animal: "Le Gecko", icon: GeckoIcon, innovation: "Adhésifs réutilisables", description: "Les millions de poils microscopiques sur les pattes du gecko ont inspiré des adhésifs puissants ne laissant aucun résidu.", color: "var(--maquis)" },
  { animal: "La Feuille de Lotus", icon: Droplets, innovation: "Surfaces auto-nettoyantes", description: "La structure nano de la feuille de lotus repousse l'eau et la saleté, inspirant des peintures et textiles autonettoyants.", color: "var(--maquis-light)" },
  { animal: "La Baleine à bosse", icon: Fish, innovation: "Pales d'éoliennes", description: "Les tubercules sur les nageoires des baleines ont inspiré des pales d'éoliennes plus efficaces et silencieuses.", color: "var(--terracotta-dark)" },
  { animal: "L'œil du homard", icon: Eye, innovation: "Télescopes à rayons X", description: "La structure de l'œil du homard a inspiré des télescopes capables de détecter les rayons X dans l'espace.", color: "var(--dusty-rose)" }
];

const biomimicryQuiz = [
  { id: 1, question: "Qu'est-ce que le biomimétisme ?", context: "Définition", options: [{ text: "L'étude des animaux en laboratoire", correct: false }, { text: "L'imitation des modèles de la nature pour résoudre des problèmes", correct: true }, { text: "La protection des espèces menacées", correct: false }, { text: "Le clonage d'organismes vivants", correct: false }], explanation: "Le biomimétisme s'inspire des solutions développées par la nature au cours de 3,8 milliards d'années d'évolution." },
  { id: 2, question: "Quel animal a inspiré la forme du TGV japonais Shinkansen ?", context: "Applications", options: [{ text: "L'aigle", correct: false }, { text: "Le martin-pêcheur", correct: true }, { text: "Le faucon", correct: false }, { text: "Le colibri", correct: false }], explanation: "Le bec du martin-pêcheur permet de passer de l'air à l'eau sans éclaboussure." },
  { id: 3, question: "Quel principe naturel a inspiré le Velcro ?", context: "Inventions célèbres", options: [{ text: "Les écailles de poisson", correct: false }, { text: "Les crochets des graines de bardane", correct: true }, { text: "Les ventouses de pieuvre", correct: false }, { text: "Les pattes de gecko", correct: false }], explanation: "George de Mestral a inventé le Velcro après avoir observé les graines de bardane." },
  { id: 4, question: "Comment les termites ont-ils inspiré l'architecture durable ?", context: "Architecture", options: [{ text: "Par leur capacité à creuser des tunnels", correct: false }, { text: "Par leur système de ventilation naturelle", correct: true }, { text: "Par leur résistance aux prédateurs", correct: false }, { text: "Par leur organisation sociale", correct: false }], explanation: "L'Eastgate Centre au Zimbabwe utilise le système de ventilation des termitières." },
  { id: 5, question: "Quel est l'avantage principal du biomimétisme pour les entreprises ?", context: "Avantages stratégiques", options: [{ text: "Il permet uniquement de réduire les coûts", correct: false }, { text: "Il offre des solutions innovantes, efficaces et durables", correct: true }, { text: "Il n'a d'intérêt que pour les laboratoires", correct: false }, { text: "Il ne s'applique qu'à la mode", correct: false }], explanation: "Le biomimétisme permet d'innover en s'inspirant de solutions éprouvées par l'évolution." }
];

const reflexes = [
  { icon: Target, title: "Raisonner avec intention", text: "Clarifier ses objectifs pour donner du sens et de l'impact à chaque démarche d'engagement." },
  { icon: Search, title: "Cultiver son esprit critique", text: "Interroger les sources, vérifier les données et appréhender les enjeux complexes sans préjugés." },
  { icon: ShieldCheck, title: "S'accorder le droit à l'erreur", text: "Adopter l'état d'esprit « Soit je réussis, soit j'apprends » pour progresser sereinement." },
  { icon: Pause, title: "Prendre des temps d'ancrage et de silence", text: "S'accorder des pauses pour assimiler les connaissances et construire une réflexion profonde." },
  { icon: User, title: "Mieux se connaître", text: "Identifier ses leviers d'action personnels et professionnels pour agir là où on est le plus utile." },
  { icon: MessageCircle, title: "(Se) faire des feedbacks", text: "Partager ce qui fonctionne et ce qui doit être ajusté pour grandir ensemble." },
  { icon: Footprints, title: "Adopter les Prochains Petits Pas (PPP)", text: "Découper les grands défis écologiques en actions concrètes et réalisables dès aujourd'hui." },
];

const InnovationResponsable = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { getField } = usePageContent("innovation");
  usePageTitle("Méthodologie");
  const [selectedExample, setSelectedExample] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleStartQuiz = () => {
    setQuizStarted(true);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  const handleAnswer = (option, index) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    setShowExplanation(true);
    const newAnswers = { ...answers, [currentQuestion]: { ...option, questionId: biomimicryQuiz[currentQuestion].id } };
    setAnswers(newAnswers);
    if (option.correct) setScore(prev => prev + 1);
  };

  const handleNext = () => {
    if (currentQuestion < biomimicryQuiz.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setShowResults(true);
      if (isAuthenticated) saveQuizResult();
    }
  };

  const saveQuizResult = async () => {
    try {
      await axios.post(`${API}/quiz/result`, {
        quiz_type: "biomimicry",
        score: score + (selectedAnswer !== null && biomimicryQuiz[currentQuestion].options[selectedAnswer].correct ? 1 : 0),
        max_score: biomimicryQuiz.length,
        answers
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Résultats sauvegardés !");
    } catch (error) {
      console.error("Failed to save results:", error);
    }
  };

  const progress = ((currentQuestion + 1) / biomimicryQuiz.length) * 100;
  const question = biomimicryQuiz[currentQuestion];
  const percentage = Math.round((score / biomimicryQuiz.length) * 100);

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="innovation-responsable-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--maquis)]/15 top-0 left-0"></div>
        <div className="blob-bg w-[400px] h-[400px] bg-[var(--dusty-rose)] bottom-0 right-0"></div>

        <div className="container-premium relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--charcoal)] mb-6 leading-tight">
              <span className="text-[var(--maquis)] emphasis">Méthodologie</span>
            </h1>
            <p className="text-xl text-[var(--stone)] mb-10 leading-relaxed">
              Utilisez les clés de l'action au quotidien pour mieux s'engager.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content - visible when quiz is not active */}
      {!quizStarted && !showResults && (
        <>
          {/* Method Introduction + Photos */}
          <section className="pb-20" data-testid="method-section">
            <div className="container-premium">
              <div className="max-w-5xl mx-auto">
                {/* Photo Gallery */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-16">
                  <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                    <img src={PHOTO_1} alt="Atelier pédagogique — Nourrir Demain" className="w-full h-full object-cover" />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                    <img src={PHOTO_2} alt="Maud en formation — enjeux climatiques" className="w-full h-full object-cover" />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3] col-span-2 md:col-span-1">
                    <img src={PHOTO_3} alt="Maud — carte Alimentation CO2" className="w-full h-full object-cover" />
                  </div>
                </div>

                {/* Method Text */}
                <div className="max-w-3xl mx-auto">
                  <div className="card-premium p-8 md:p-12 mb-12 border-l-4 border-[var(--maquis)]">
                    <p className="font-display text-2xl md:text-3xl text-[var(--charcoal)] leading-relaxed">
                      Ma méthode repose sur un principe central : <span className="text-[var(--maquis)] emphasis">« Jouer pour comprendre, donner les clés pour agir »</span>.
                    </p>
                    <p className="text-lg text-[var(--stone)] leading-relaxed mt-6">
                      J'utilise la pédagogie active et l'intelligence collective pour que chaque participant (petit ou grand) devienne acteur de son apprentissage.
                    </p>
                  </div>

                  <p className="text-lg text-[var(--stone)] leading-relaxed mb-10">
                    Inspirée du Kit de l'apprentissage et de l'engagement, j'aide chaque personne et chaque équipe à développer <strong className="text-[var(--charcoal)]">7 réflexes fondamentaux</strong> :
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 7 Reflexes */}
          <section className="pb-20 bg-[var(--sand)]" data-testid="reflexes-section">
            <div className="container-premium py-20">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-14">
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--charcoal)]">
                    Les 7 réflexes <span className="emphasis">fondamentaux</span>
                  </h2>
                </div>

                <div className="space-y-5">
                  {reflexes.map((reflex, idx) => (
                    <div key={idx} className="card-premium p-6 md:p-8 flex items-start gap-5" data-testid={`reflex-${idx}`}>
                      <div className="w-12 h-12 bg-[var(--maquis)]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <reflex.icon className="w-6 h-6 text-[var(--maquis)]" />
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-bold text-[var(--charcoal)] mb-1">
                          {reflex.title}
                        </h3>
                        <p className="text-[var(--stone)] leading-relaxed">{reflex.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Biomimicry Examples */}
          <section className="pb-20 section-premium" data-testid="biomimicry-section">
            <div className="container-premium">
              <div className="max-w-4xl mx-auto">
                <h2 className="font-display text-3xl font-bold text-[var(--charcoal)] mb-8">
                  Exemples inspirants du <span className="emphasis">vivant</span>
                </h2>

                <div className="flex flex-wrap gap-3 mb-8">
                  {biomimicryExamples.map((example, index) => {
                    const Icon = example.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => setSelectedExample(index)}
                        data-testid={`biomimicry-tab-${index}`}
                        className={`flex items-center gap-2 px-5 py-3 rounded-full transition-all font-medium ${
                          selectedExample === index
                            ? 'text-[var(--bone)]'
                            : 'bg-[var(--sand)] text-[var(--charcoal)] hover:bg-[var(--sand-dark)]'
                        }`}
                        style={selectedExample === index ? { backgroundColor: example.color } : {}}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{example.animal}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="card-premium overflow-hidden" data-testid="selected-biomimicry-example">
                  <div className="grid md:grid-cols-2">
                    <div 
                      className="h-64 md:h-auto min-h-[300px] flex items-center justify-center"
                      style={{ backgroundColor: `${biomimicryExamples[selectedExample].color}15` }}
                    >
                      {(() => {
                        const Icon = biomimicryExamples[selectedExample].icon;
                        return <Icon className="w-32 h-32 animate-float" style={{ color: biomimicryExamples[selectedExample].color }} />;
                      })()}
                    </div>
                    <div className="p-8 md:p-10">
                      <span 
                        className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4"
                        style={{ backgroundColor: `${biomimicryExamples[selectedExample].color}15`, color: biomimicryExamples[selectedExample].color }}
                      >
                        Innovation inspirée
                      </span>
                      <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-3">
                        {biomimicryExamples[selectedExample].animal}
                      </h3>
                      <p className="text-lg font-medium mb-4" style={{ color: biomimicryExamples[selectedExample].color }}>
                        → {biomimicryExamples[selectedExample].innovation}
                      </p>
                      <p className="text-[var(--stone)] leading-relaxed">
                        {biomimicryExamples[selectedExample].description}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-center mt-10">
                  <button 
                    onClick={handleStartQuiz}
                    className="btn-primary flex items-center gap-3 mx-auto"
                    data-testid="start-biomimicry-quiz-btn"
                  >
                    Tester mes connaissances
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="section-premium relative overflow-hidden">
            <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/20 top-0 right-0"></div>
            <div className="container-premium relative z-10 text-center">
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)] mb-8">
                Prêt à transmettre<br />
                <span className="text-[var(--terracotta)] emphasis">ensemble</span> ?
              </h2>
              <p className="text-[var(--stone)] text-xl max-w-2xl mx-auto mb-12">
                Faisons le point ensemble sur vos enjeux et construisons un parcours adapté à votre contexte et vos attentes.
              </p>
              <Link to="/contact">
                <button className="btn-primary text-lg px-10 py-5">
                  Prendre rendez-vous
                </button>
              </Link>
            </div>
          </section>
        </>
      )}

      {/* Quiz Section */}
      {quizStarted && !showResults && (
        <section className="pb-20">
          <div className="container-premium">
            <div className="max-w-3xl mx-auto">
              <div className="glass-warm rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-[var(--stone)]">Question {currentQuestion + 1} / {biomimicryQuiz.length}</span>
                  <span className="text-sm font-medium text-[var(--maquis)]">Score: {score}</span>
                </div>
                <div className="h-2 bg-[var(--sand)] rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--maquis)] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>

              <div className="card-premium overflow-hidden" data-testid="biomimicry-quiz-card">
                <div className="p-8 md:p-10">
                  <div className="mb-8">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--maquis)]">{question.context}</span>
                    <h3 className="font-display text-2xl font-semibold text-[var(--charcoal)] mt-2">{question.question}</h3>
                  </div>

                  <div className="space-y-3">
                    {question.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswer(option, index)}
                        disabled={selectedAnswer !== null}
                        data-testid={`biomimicry-option-${index}`}
                        className={`w-full text-left quiz-option-premium flex items-center gap-4 ${
                          selectedAnswer === index 
                            ? option.correct ? 'correct' : 'incorrect'
                            : selectedAnswer !== null && option.correct ? 'correct' : ''
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          selectedAnswer !== null
                            ? option.correct ? 'bg-[var(--maquis)]/10 text-[var(--maquis)]' : selectedAnswer === index ? 'bg-[var(--terracotta)]/10 text-[var(--terracotta)]' : 'bg-[var(--sand)]'
                            : 'bg-[var(--sand)]'
                        }`}>
                          {selectedAnswer !== null ? (
                            option.correct ? <CheckCircle className="w-5 h-5" /> : 
                            selectedAnswer === index ? <XCircle className="w-5 h-5" /> : 
                            <span className="text-sm font-medium">{String.fromCharCode(65 + index)}</span>
                          ) : (
                            <span className="text-sm font-medium text-[var(--stone)]">{String.fromCharCode(65 + index)}</span>
                          )}
                        </div>
                        <span className="flex-1 text-[var(--charcoal)]">{option.text}</span>
                      </button>
                    ))}
                  </div>

                  {showExplanation && (
                    <div className="mt-8 p-6 bg-[var(--sand)] rounded-2xl animate-fade-in">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-[var(--maquis)] mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-semibold text-[var(--charcoal)]">Explication : </span>
                          <span className="text-[var(--stone)]">{question.explanation}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {selectedAnswer !== null && (
                  <div className="px-8 md:px-10 pb-8 md:pb-10">
                    <button onClick={handleNext} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="biomimicry-next-btn">
                      {currentQuestion < biomimicryQuiz.length - 1 ? "Question suivante" : "Voir les résultats"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Results */}
      {showResults && (
        <section className="pb-20">
          <div className="container-premium">
            <div className="max-w-2xl mx-auto card-premium overflow-hidden" data-testid="biomimicry-results">
              <div className={`p-10 text-center ${percentage >= 70 ? 'bg-[var(--maquis)]/10' : 'bg-[var(--terracotta)]/10'}`}>
                <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 bg-[var(--bone)]">
                  <Brain className={`w-12 h-12 ${percentage >= 70 ? 'text-[var(--maquis)]' : 'text-[var(--terracotta)]'}`} />
                </div>
                <h2 className="font-display text-3xl font-bold text-[var(--charcoal)] mb-2">
                  {percentage >= 70 ? "Expert en biomimétisme !" : percentage >= 40 ? "Bon début !" : "Découverte en cours !"}
                </h2>
                <p className="text-[var(--stone)]">Vous avez obtenu {score} / {biomimicryQuiz.length} ({percentage}%)</p>
              </div>
              <div className="p-10">
                <p className="text-[var(--stone)] text-center mb-8">
                  La nature a optimisé ses solutions pendant des milliards d'années. S'en inspirer, c'est accéder à un réservoir d'innovations testées et durables.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={handleStartQuiz} className="btn-secondary flex-1">Refaire le quiz</button>
                  <button onClick={() => navigate('/contact')} className="btn-primary flex-1 flex items-center justify-center gap-2">
                    Être accompagné <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                {!isAuthenticated && (
                  <p className="text-center text-sm text-[var(--stone)] mt-6">
                    <a href="/auth" className="text-[var(--terracotta)] font-medium hover:underline">Créez votre espace</a> pour sauvegarder vos résultats
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default InnovationResponsable;
